import { api } from './client';
import type { IntelListResponse, IntelReportDto, IntelType } from '../types/intel';

// --- Target Finder types ---

export interface SnapshotSearchParams {
  raceIn?: string[];
  rankIn?: string[];
  nwMin?: number;
  nwMax?: number;
  landMin?: number;
  landMax?: number;
  nwpaMin?: number;
  nwpaMax?: number;
  kdNwMin?: number;
  kdNwMax?: number;
  kdLandMin?: number;
  kdLandMax?: number;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
  page?: number;
  size?: number;
}

export interface ProvinceSnapshotDto {
  provinceId: string;
  provinceName: string;
  location: string;
  race: string;
  rank: string;
  networth: number;
  land: number;
  nwpa: number;
  kingdomId: string;
  kingdomName: string;
  kingdomNw: number;
  kingdomLand: number;
  // SF-105: optional + Number.isFinite guard per the Redis-cached-DTO-field convention -
  // cached entries written before deployment will lack this field.
  protectionExpiresAtTick?: number;
}

export interface PlayerStats {
  networth: number;
  land: number;
  nwpa: number;
  kingdomNW: number;
  kingdomLand: number;
}

export interface SnapshotSearchResponse {
  results: ProvinceSnapshotDto[];
  totalCount: number;
  page: number;
  size: number;
  player: PlayerStats | null;
  // SF-105: Target Finder has no other source for the current tick to compare against.
  currentTick: number;
}

export interface GetIntelParams {
  target?: string; // targetProvinceId filter
  type?: IntelType;
  page?: number;
  size?: number;
}

export interface GetKingdomIntelParams {
  targetProvince?: string;
  targetKingdom?: string;
  page?: number;
  size?: number;
}

/**
 * Get my intel reports
 * GET /api/worlds/{worldId}/intel
 */
export async function getMyIntel(worldId: string, params: GetIntelParams = {}): Promise<IntelListResponse> {
  const queryParams = new URLSearchParams();

  if (params.target) queryParams.append('target', params.target);
  if (params.type) queryParams.append('type', params.type);
  if (params.page !== undefined) queryParams.append('page', params.page.toString());
  if (params.size !== undefined) queryParams.append('size', params.size.toString());

  const query = queryParams.toString();
  const url = query ? `/api/worlds/${worldId}/intel?${query}` : `/api/worlds/${worldId}/intel`;

  const response = await api.get<IntelListResponse>(url);

  if (response.error) {
    throw new Error(response.error);
  }

  return response.data!;
}

/**
 * Get kingdom intel reports
 * GET /api/worlds/{worldId}/intel/kingdom
 */
export async function getKingdomIntel(worldId: string, params: GetKingdomIntelParams = {}): Promise<IntelListResponse> {
  const queryParams = new URLSearchParams();

  if (params.targetProvince) queryParams.append('targetProvince', params.targetProvince);
  if (params.targetKingdom) queryParams.append('targetKingdom', params.targetKingdom);
  if (params.page !== undefined) queryParams.append('page', params.page.toString());
  if (params.size !== undefined) queryParams.append('size', params.size.toString());

  const query = queryParams.toString();
  const url = query
    ? `/api/worlds/${worldId}/intel/kingdom?${query}`
    : `/api/worlds/${worldId}/intel/kingdom`;

  const response = await api.get<IntelListResponse>(url);

  if (response.error) {
    throw new Error(response.error);
  }

  return response.data!;
}

/**
 * Get single intel report
 * GET /api/worlds/{worldId}/intel/{id}
 */
export async function getIntelReport(worldId: string, id: string): Promise<IntelReportDto> {
  const response = await api.get<IntelReportDto>(`/api/worlds/${worldId}/intel/${id}`);

  if (response.error) {
    throw new Error(response.error);
  }

  return response.data!;
}

/**
 * Share intel report with kingdom
 * POST /api/worlds/{worldId}/intel/{id}/share
 */
export async function shareIntelReport(worldId: string, id: string): Promise<IntelReportDto> {
  const response = await api.post<IntelReportDto>(`/api/worlds/${worldId}/intel/${id}/share`, {
    sharing: 'SHARED',
  });

  if (response.error) {
    throw new Error(response.error);
  }

  return response.data!;
}

/**
 * Delete intel report
 * DELETE /api/worlds/{worldId}/intel/{id}
 */
export async function deleteIntelReport(worldId: string, id: string): Promise<void> {
  const response = await api.delete(`/api/worlds/${worldId}/intel/${id}`);

  if (response.error) {
    throw new Error(response.error);
  }
}

/**
 * Search province snapshots for Target Finder
 * GET /api/worlds/{worldId}/intel/search
 */
export async function searchSnapshots(
  worldId: string,
  params: SnapshotSearchParams = {}
): Promise<SnapshotSearchResponse> {
  const queryParams = new URLSearchParams();

  if (params.raceIn?.length) queryParams.append('raceIn', params.raceIn.join(','));
  if (params.rankIn?.length) queryParams.append('rankIn', params.rankIn.join(','));
  if (params.nwMin !== undefined) queryParams.append('nwMin', params.nwMin.toString());
  if (params.nwMax !== undefined) queryParams.append('nwMax', params.nwMax.toString());
  if (params.landMin !== undefined) queryParams.append('landMin', params.landMin.toString());
  if (params.landMax !== undefined) queryParams.append('landMax', params.landMax.toString());
  if (params.nwpaMin !== undefined) queryParams.append('nwpaMin', params.nwpaMin.toString());
  if (params.nwpaMax !== undefined) queryParams.append('nwpaMax', params.nwpaMax.toString());
  if (params.kdNwMin !== undefined) queryParams.append('kdNwMin', params.kdNwMin.toString());
  if (params.kdNwMax !== undefined) queryParams.append('kdNwMax', params.kdNwMax.toString());
  if (params.kdLandMin !== undefined) queryParams.append('kdLandMin', params.kdLandMin.toString());
  if (params.kdLandMax !== undefined) queryParams.append('kdLandMax', params.kdLandMax.toString());
  if (params.sortBy) queryParams.append('sortBy', params.sortBy);
  if (params.sortDir) queryParams.append('sortDir', params.sortDir);
  if (params.page !== undefined) queryParams.append('page', params.page.toString());
  if (params.size !== undefined) queryParams.append('size', params.size.toString());

  const query = queryParams.toString();
  const url = query
    ? `/api/worlds/${worldId}/intel/search?${query}`
    : `/api/worlds/${worldId}/intel/search`;

  const response = await api.get<SnapshotSearchResponse>(url);

  if (response.error) {
    throw new Error(response.error);
  }

  return response.data!;
}
